import { Fragment, FunctionComponent, type ReactNode, useState } from 'react';

import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '@design-system';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { OnboardingDownloadReport } from '@/pages/OnboardingDownloadReport';
import { OnboardingOverview } from '@/pages/OnboardingOverview';

import { VscSend } from 'react-icons/vsc';
import { MdDelete } from 'react-icons/md';
import { IconType } from 'react-icons/lib';
import { RiLineChartLine } from 'react-icons/ri';
import { IoDocumentTextOutline } from 'react-icons/io5';
import {
  CircleUserRound,
  Loader2,
  LucideProps,
  MoreHorizontal,
  Share2,
  Users,
  Link,
  UserPlus,
  CopyPlus,
  FileEdit,
  ScrollText,
  RefreshCw,
} from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigateToPage } from '@client';
import { useIntakeFormActions } from '@client/hooks/useIntakeFormActions';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { SessionType, ClientStatus } from '@shared';
import { selectSession } from '@/model/selectors';
import { ModalState, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';
import { IncompleteIntakeDialog } from '@/shared/components/incomplete-intake-dialog';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useIsFreemiumAdvisor } from '@/shared/hooks/use-freemium-advisor';
import { useIsGenworthMode } from '@/features/genworth/hooks/use-genworth-mode';
import { useValidatedIntakeNavigation } from '@/shared/hooks/use-validated-intake-navigation';
import {
  useDistributionPartnerLegacyShareConsent,
  useIsDistributionPartnersForEdj,
} from '@/features/distribution-partners';
import { PilotRestricted, UpdateIntakeFormPilotRestricted } from './pilot-restricted';
import { NylPilotClientCreationRestricted } from './nyl-pilot-client-creation-restricted';
import {
  useCanEditEdjQuickEstimate,
  useCanRetryEdjQuickEstimate,
} from '@/features/client-quick-plan/hooks/use-can-edit-edj-quick-estimate';
import { useRecalculateQuickEstimate } from '@/features/client-quick-plan/hooks/use-recalculate-quick-estimate';

export function ClientActionMenu({
  client,
  sessionAdvisor,
  setModalState,
}: {
  client: Client;
  sessionAdvisor: Advisor;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}) {
  const session = useSelector(selectSession);
  const { navigateToUpdateIntakeForm } = useIntakeFormActions();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;
  const shareSpecificClientsEnabled = flags.shareSpecificClients ?? false;
  const isEdjPilot = useIsEdjPilotAdvisor();
  const isDistributionPartnersForEdj = useIsDistributionPartnersForEdj();
  const isFreemiumAdvisor = useIsFreemiumAdvisor();
  const isGenworthMode = useIsGenworthMode();

  // hasConnections gates the per-client share action: a connected partner contact can be shared
  // with one client at a time even when auto-sharing is off for them.
  const { hasConnections } = useDistributionPartnerLegacyShareConsent();

  const hasFormReference = !!client.formReferenceId;
  const isClientActive = client.clientStatus === ClientStatus.active;
  const showIntakeFormAction = useFormProvider && hasFormReference;
  const buttonText = isClientActive ? 'Update Intake Form' : 'Complete Form';
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const canEditEdjQuickEstimate = useCanEditEdjQuickEstimate(client);
  const canRetryEdjQuickEstimate = useCanRetryEdjQuickEstimate(client);
  const { recalculateQuickEstimate } = useRecalculateQuickEstimate();

  function pickClientActions(client: Client) {
    const viewResultsAction = (
      <PageLinkAction
        to={OnboardingOverview}
        client={client}
        sessionAdvisor={sessionAdvisor}
        icon={RiLineChartLine}
        onIncompleteIntake={() => setIncompleteDialogOpen(true)}
      >
        View Results
      </PageLinkAction>
    );

    const skipToDownloadReportAction = (
      <PageLinkAction
        to={OnboardingDownloadReport}
        client={client}
        sessionAdvisor={sessionAdvisor}
        icon={IoDocumentTextOutline}
      >
        Download Report
      </PageLinkAction>
    );

    const hasPartner = !!client.partnerClientId;

    const editPartnerAction = (
      <ButtonAction
        id="edit-partner-menuitem"
        onClick={() => setModalState({ type: 'editPartner', client })}
        icon={Users}
      >
        Edit Partner
      </ButtonAction>
    );

    const linkExistingPartnerAction = (
      <ButtonAction
        id="link-partner-menuitem"
        onClick={() => setModalState({ type: 'linkPartner', client })}
        icon={Link}
      >
        Link Existing Client as Partner
      </ButtonAction>
    );

    const addNewPartnerAction = (
      <NylPilotClientCreationRestricted capability="add-partner">
        <ButtonAction
          id="add-partner-menuitem"
          onClick={() => setModalState({ type: 'addPartner', client })}
          icon={UserPlus}
        >
          Add New Partner
        </ButtonAction>
      </NylPilotClientCreationRestricted>
    );

    const partnerActions = hasPartner
      ? [editPartnerAction]
      : [linkExistingPartnerAction, addNewPartnerAction];

    const sendClientSigninLinkAction = (
      <ButtonAction
        onClick={() => setModalState({ type: 'sendClientSigninLink', client })}
        icon={isEdjPilot ? Link : VscSend}
      >
        {isEdjPilot ? 'Copy Access Link' : 'Send Access Link'}
      </ButtonAction>
    );

    const deleteClientAction = (
      <ButtonAction
        onClick={() => setModalState({ type: 'delete', client })}
        icon={MdDelete}
        className="text-red-700"
      >
        Delete Client
      </ButtonAction>
    );

    const viewClientDataAction = (
      <PilotRestricted capability="intake-form-data">
        <ButtonAction
          onClick={() => setModalState({ type: 'clientData', client })}
          icon={CircleUserRound}
        >
          Intake Form Data
        </ButtonAction>
      </PilotRestricted>
    );

    const editPolicyDetailsAction = (
      <ButtonAction
        onClick={() => setModalState({ type: 'editPolicyDetails', client })}
        icon={ScrollText}
      >
        Edit Policy Details
      </ButtonAction>
    );

    const quickEditIntakeAction = (
      <PilotRestricted capability="quick-edit-intake">
        <ButtonAction
          onClick={() => setModalState({ type: 'clientDataEdit', client })}
          icon={FileEdit}
        >
          Update Intake Form
        </ButtonAction>
      </PilotRestricted>
    );

    const editEdjQuickEstimateAction = (
      <ButtonAction
        onClick={() => setModalState({ type: 'edjQuickEstimateEdit', client })}
        icon={FileEdit}
      >
        Edit Quick Estimate
      </ButtonAction>
    );

    const retryEdjQuickEstimateAction = (
      <ButtonAction
        id="retry-quick-estimate-menuitem"
        onClick={() => recalculateQuickEstimate(client.clientId)}
        icon={RefreshCw}
      >
        Retry Estimate
      </ButtonAction>
    );

    const navigateIntakeFormAction = (
      <UpdateIntakeFormPilotRestricted client={client}>
        <ButtonAction
          onClick={async () => {
            await navigateToUpdateIntakeForm(client.clientId);
          }}
          icon={FileEdit}
        >
          {buttonText}
        </ButtonAction>
      </UpdateIntakeFormPilotRestricted>
    );

    const newDuplicateClientAction = (
      <ButtonAction
        onClick={() => setModalState({ type: 'newDuplicateClient', client })}
        icon={CopyPlus}
      >
        Duplicate Client
      </ButtonAction>
    );

    const shareWithSupervisorAction = (
      <PilotRestricted
        capability="client-sharing"
        side="left"
      >
        <ButtonAction
          id="share-with-supervisor-menuitem"
          onClick={() => setModalState({ type: 'shareWithSupervisor', client })}
          icon={Share2}
        >
          {isEdjPilot ? 'Collaborate with partner' : 'Share with Supervisor'}
        </ButtonAction>
      </PilotRestricted>
    );

    const isOwnClient = client.advisorId === sessionAdvisor.advisorId;

    // Per-client share visibility:
    // - EDJ advisors share individual clients only under the reframed EDJ election experience
    //   (distributionPartnersForEdj); otherwise they auto-share going forward and don't share per-client.
    // - Everyone else can share any of their own clients — new-consent or legacy — one at a time
    //   with a connected partner contact. Sharing is per-contact now, so the batch legacy
    //   attestation no longer means a client is "already shared", and the action stays available.
    const canShareClient =
      (!isEdjPilot || isDistributionPartnersForEdj) &&
      (shareSpecificClientsEnabled || hasConnections) &&
      isOwnClient &&
      !client.isSampleClient;

    const fullActions = [
      viewResultsAction,
      ...partnerActions,
      // Freemium advisors don't get report downloads from the dashboard, except
      // for sample clients, which always have full access.
      ...(isFreemiumAdvisor && !client.isSampleClient ? [] : [skipToDownloadReportAction]),
      viewClientDataAction,
      ...(canEditEdjQuickEstimate ? [editEdjQuickEstimateAction] : []),
      ...(showIntakeFormAction ? [quickEditIntakeAction] : []),
      ...(isGenworthMode ? [editPolicyDetailsAction] : []),
      ...(isGenworthMode ? [] : [sendClientSigninLinkAction]),
      ...(canShareClient ? [shareWithSupervisorAction] : []),
      ...(session.sessionType === SessionType.wizard ? [newDuplicateClientAction] : []),
      deleteClientAction,
    ];

    const clientTags = client.clientTags;
    if (clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag))) {
      // A failed quick estimate is retried as it is, or fixed by editing its answers; both re-run
      // the models.
      const errorStateActions = [
        ...(canRetryEdjQuickEstimate ? [retryEdjQuickEstimateAction] : []),
        ...(canEditEdjQuickEstimate ? [editEdjQuickEstimateAction] : []),
        ...(showIntakeFormAction ? [navigateIntakeFormAction] : []),
        deleteClientAction,
      ];
      return errorStateActions;
    }
    return fullActions;
  }

  const actions = pickClientActions(client);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger>
          <Button
            variant="ghost"
            size="icon-sm"
            data-testid="client-action-menu-trigger"
          >
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-testid="client-actions-menu-item-container"
          align="end"
          className="w-max"
        >
          <ActionMenu actions={actions} />
        </DropdownMenuContent>
      </DropdownMenu>
      <IncompleteIntakeDialog
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
        client={client}
      />
    </>
  );
}

function ActionMenu({ actions }: { actions: ReactNode[] }) {
  return (
    <>
      {actions.map((action, index) => (
        <Fragment key={index}>
          {index === actions.length - 1 && index !== 0 && <DropdownMenuSeparator />}
          {action}
        </Fragment>
      ))}
    </>
  );
}

function PageLinkAction({
  to,
  icon: Icon,
  client,
  sessionAdvisor,
  children,
  onIncompleteIntake,
}: {
  to: FunctionComponent<ClientIdProps>;
  icon: IconType;
  sessionAdvisor: Advisor;
  client: Client;
  children: React.ReactNode;
  onIncompleteIntake?: () => void;
}) {
  const navigateToPage = useNavigateToPage();
  const { validateAndDecide, isValidating } = useValidatedIntakeNavigation(client);

  const handleNavigate = async () => {
    if (!onIncompleteIntake) {
      navigateToPage(to, { clientId: client.clientId });
      return;
    }
    const { needsFullIntake } = await validateAndDecide();
    if (needsFullIntake) {
      onIncompleteIntake();
    } else {
      navigateToPage(to, { clientId: client.clientId });
    }
  };

  const menuItem = (
    <DropdownMenuItem
      onClick={handleNavigate}
      disabled={isValidating}
      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
    >
      {isValidating ? (
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
      ) : (
        <Icon className="text-muted-foreground size-4" />
      )}
      {children}
    </DropdownMenuItem>
  );

  if (sessionAdvisor.advisorId !== client.advisorId) {
    return (
      <AdvisorWarningDialog
        title="Viewing another advisor's client"
        message="Warning: Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
        onContinue={handleNavigate}
      >
        <Button
          variant="ghost"
          className={cn(
            'focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 relative flex w-full cursor-default select-none items-center justify-start gap-2 rounded-sm px-3 py-1.5 text-sm outline-none transition-colors [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
          )}
        >
          <Icon className="text-muted-foreground mr-1 size-4" />
          {children}
        </Button>
      </AdvisorWarningDialog>
    );
  }

  return menuItem;
}

function ButtonAction({
  onClick,
  icon: Icon,
  children,
  className,
  id,
}: {
  onClick: () => void;
  id?: string;
  icon:
    | IconType
    | React.ForwardRefExoticComponent<
        Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
      >;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <DropdownMenuItem
      className={cn('flex w-full items-center gap-3 px-3 py-2 text-left text-sm', className)}
      data-testid={id}
      onClick={onClick}
    >
      <Icon className="text-muted-foreground size-4" />
      {children}
    </DropdownMenuItem>
  );
}
